import { color } from '@/shared/styles/assets/colors';
import create from 'zustand';

/** Stable ids for the panels that share the docked edge. */
export const DOCKED_PANEL_IDS = {
  ALERT_REVIEW: 'alert-review',
  ADD_ACTIVITY: 'add-activity',
  SOAP_NOTES: 'soap-notes',
  PATIENT_INFO: 'patient-info',
  READINGS_DRAWER: 'readings-drawer'
} as const;

export type DockedPanel = {
  id: string;
  title: string;
};

/**
 * One accent per docked panel, taken from the logo: navy, then salmon, then cyan.
 *
 * A panel keeps its accent wherever it appears -- the tab that switches to it while the dock is
 * open, and the handle that restores it while the dock is collapsed -- so the colour, not the
 * reading, is what tells the two apart.
 *
 * The selected tab writes white on its accent, so every accent must clear 4.5:1 against white.
 * The logo salmon (#ed8074) is 2.64:1, so its slot takes a deeper salmon: 8.00, 5.38, 4.98.
 */
export const DOCKED_PANEL_ACCENTS = [color.truentityBlue[500], color.alertTextSalmon, color.truentityCyan[500]] as const;

/*
 * Each known panel owns its accent, so it looks the same whatever opened first and its tab is the
 * colour of its header: Add Activity the brand blue, Alert Review the logo salmon, Patient Info
 * a warm blue, SOAP Notes cyan.
 */
const PANEL_ACCENTS: Record<string, string> = {
  [DOCKED_PANEL_IDS.ADD_ACTIVITY]: color.truentityBlue[500],
  [DOCKED_PANEL_IDS.ALERT_REVIEW]: color.truentitySalmon[500],
  [DOCKED_PANEL_IDS.SOAP_NOTES]: color.truentityCyan[500],
  [DOCKED_PANEL_IDS.PATIENT_INFO]: color.panelWarmBlue
};

/*
 * The text written on a panel's accent. White, except on the logo salmon, where white is 2.64:1;
 * there it is the panel navy (6.3:1), the same as the Alert Review header's own text.
 */
const PANEL_ACCENT_TEXT: Record<string, string> = {
  [DOCKED_PANEL_IDS.ALERT_REVIEW]: color.panelNavy
};

export const accentTextForPanel = (id: string): string => PANEL_ACCENT_TEXT[id] ?? color.white;

/**
 * The panels in the order their tabs are shown: alphabetical by tab title, so a panel's tab is in
 * the same place whatever order the panels were opened in. The store keeps opening order, which is
 * what closing the visible panel falls back through.
 */
export const panelsInTabOrder = (panels: DockedPanel[]): DockedPanel[] =>
  [...panels].sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));

export const accentForPanel = (panels: DockedPanel[], id: string): string => {
  if (PANEL_ACCENTS[id]) return PANEL_ACCENTS[id];
  // A panel without its own accent falls back to its position in the stack.
  const index = panels.findIndex(panel => panel.id === id);
  return DOCKED_PANEL_ACCENTS[(index < 0 ? 0 : index) % DOCKED_PANEL_ACCENTS.length];
};

export type DockedPanelStore = {
  dockedWidth: number;
  setDockedWidth: (value: number) => void;
  isDockSuppressed: boolean;
  setIsDockSuppressed: (value: boolean) => void;
  // Docked / collapsed / resized state lives here rather than in BaseDraggableDialog so the panel
  // comes back exactly the way the user left it after the dialog unmounts — e.g. moving between
  // patient pages, or leaving the patient section and returning. Only closing the dialog resets it.
  isDocked: boolean;
  setIsDocked: (value: boolean) => void;
  isDockCollapsed: boolean;
  setIsDockCollapsed: (value: boolean) => void;
  dockedPanelWidth: number;
  setDockedPanelWidth: (value: number) => void;
  /*
   * Which panels have applied their one-time `defaultDocked` snap. Guards against a remount --
   * navigating between patient pages -- re-docking a panel the user deliberately undocked, and is
   * cleared on close so the next open starts docked again.
   *
   * Per panel, not one shared flag: with two dialogs mounted, one clearing a shared flag on close
   * while the other set it on open put React into an endless update loop.
   */
  initializedPanels: string[];
  markPanelInitialized: (key: string) => void;
  clearPanelInitialized: (key: string) => void;

  /*
   * Several dialogs can be docked at once — reviewing an alert while a time-log panel stays open,
   * for instance — but only one occupies the edge at a time. The others stay mounted and hidden so
   * their in-progress edits survive, and a tab strip switches between them.
   *
   * Registration order is preserved so the tabs do not reshuffle as panels come and go.
   */
  panels: DockedPanel[];
  activePanelId: string | null;
  registerPanel: (panel: DockedPanel) => void;
  unregisterPanel: (id: string) => void;
  setActivePanel: (id: string) => void;

  resetDockState: () => void;
};

export const useDockedPanelStore = create<DockedPanelStore>(set => ({
  dockedWidth: 0,
  setDockedWidth: value => set(() => ({ dockedWidth: value })),
  isDockSuppressed: false,
  setIsDockSuppressed: value => set(() => ({ isDockSuppressed: value })),
  isDocked: false,
  setIsDocked: value => set(() => ({ isDocked: value })),
  isDockCollapsed: false,
  setIsDockCollapsed: value => set(() => ({ isDockCollapsed: value })),
  dockedPanelWidth: 480,
  setDockedPanelWidth: value => set(() => ({ dockedPanelWidth: value })),
  initializedPanels: [],
  markPanelInitialized: key =>
    set(state => (state.initializedPanels.includes(key) ? state : { initializedPanels: [...state.initializedPanels, key] })),
  clearPanelInitialized: key =>
    set(state =>
      state.initializedPanels.includes(key) ? { initializedPanels: state.initializedPanels.filter(entry => entry !== key) } : state
    ),

  panels: [],
  activePanelId: null,

  // Opening a panel brings it to the front; the one it displaced is still a tab away.
  registerPanel: panel =>
    set(state => {
      const existing = state.panels.find(entry => entry.id === panel.id);

      /*
       * Re-registering a panel already in the stack only refreshes its tab. It used to make that
       * panel active again, and a panel re-registers whenever its title is rebuilt -- any re-render
       * of the patient layout, such as the readings page clearing an alert link's params. So an
       * alert review that had just opened over Add Activity was snatched away again a moment later,
       * which looked like the panel glitching and reloading. Only a newly opened panel takes focus.
       */
      if (existing) {
        if (existing.title === panel.title) return state;
        return { panels: state.panels.map(entry => (entry.id === panel.id ? panel : entry)) };
      }

      return { panels: [...state.panels, panel], activePanelId: panel.id, isDockCollapsed: false };
    }),

  unregisterPanel: id =>
    set(state => {
      const panels = state.panels.filter(entry => entry.id !== id);
      // Closing the visible panel falls back to the most recently opened survivor rather than
      // leaving the edge showing nothing.
      const activePanelId = state.activePanelId === id ? panels[panels.length - 1]?.id ?? null : state.activePanelId;

      return { panels, activePanelId };
    }),

  setActivePanel: id => set(() => ({ activePanelId: id, isDockCollapsed: false })),

  resetDockState: () =>
    set(() => ({
      isDocked: false,
      isDockCollapsed: false,
      initializedPanels: [],
      isDockSuppressed: false,
      dockedWidth: 0,
      panels: [],
      activePanelId: null
    }))
}));
